import React, {
  memo, useMemo, useRef, useState,
} from '../../../../lib/teact/teact';

import type { ApiNetwork } from '../../../../api/types';
import type { Account, WalletCollection, WalletCollectionTabOrderId } from '../../../../global/types';

import { getAccountTitle } from '../../../../util/account';
import buildClassName from '../../../../util/buildClassName';
import buildStyle from '../../../../util/buildStyle';

import useHistoryBack from '../../../../hooks/useHistoryBack';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import useSortableList from '../../../../hooks/useSortableList';

import Button from '../../../ui/Button';
import Checkbox from '../../../ui/Checkbox';
import Draggable from '../../../ui/Draggable';
import Input from '../../../ui/Input';
import Modal from '../../../ui/Modal';
import ModalHeader from '../../../ui/ModalHeader';
import WalletAvatar from '../../../ui/WalletAvatar';

import modalStyles from '../../../ui/Modal.module.scss';
import styles from './WalletCollections.module.scss';

const COLLECTION_ROW_HEIGHT_PX = 64;

interface ManagerProps {
  isActive?: boolean;
  items: WalletCollectionManagerItem[];
  onEdit: (collectionId?: string) => void;
  onOrderChange: (orderedIds: WalletCollectionTabOrderId[]) => void;
  onBack: NoneToVoidFunction;
  onClose: NoneToVoidFunction;
}

export interface WalletCollectionManagerItem {
  id: WalletCollectionTabOrderId;
  name: string;
  walletsCount: number;
  icon: string;
  collectionId?: string;
  isSystem?: true;
}

interface EditorProps {
  isActive?: boolean;
  collection?: WalletCollection;
  accounts: Array<[string, Account]>;
  network: ApiNetwork;
  onSave: (collection: Omit<WalletCollection, 'id'> & { id?: string }) => void;
  onDelete: (collectionId: string) => void;
  onBack: NoneToVoidFunction;
  onClose: NoneToVoidFunction;
}

export function WalletCollectionsManager({
  isActive, items, onEdit, onOrderChange, onBack, onClose,
}: ManagerProps) {
  const lang = useLang();
  const scrollRef = useRef<HTMLDivElement>();
  const listRef = useRef<HTMLDivElement>();
  const collectionIds = useMemo(() => items.map(({ id }) => id), [items]);
  const { sortState, handleDrag, handleDragEnd } = useSortableList(
    collectionIds,
    COLLECTION_ROW_HEIGHT_PX,
    onOrderChange,
  );
  const orderedIndexMap = useMemo(
    () => new Map(sortState.orderedIds.map((id, index) => [id, index])),
    [sortState],
  );
  const dragOrderIndexMap = useMemo(
    () => new Map(sortState.dragOrderIds.map((id, index) => [id, index])),
    [sortState],
  );
  const canReorder = items.length > 1;

  useHistoryBack({ isActive, onBack });

  return (
    <div className={modalStyles.transitionContentWrapper}>
      <ModalHeader title={lang('Collections')} onBackButtonClick={onBack} onClose={onClose} />

      <div ref={scrollRef} className={buildClassName(styles.managerContent, 'custom-scroll')}>
        {items.length ? (
          <div
            ref={listRef}
            className={styles.collectionList}
            style={buildStyle(`height: ${items.length * COLLECTION_ROW_HEIGHT_PX}px`)}
          >
            {items.map((item, index) => {
              const isDragged = sortState.draggedIndex === index;
              const rowIndex = (isDragged ? orderedIndexMap : dragOrderIndexMap).get(item.id) ?? index;
              const content = (
                <>
                  <span className={styles.collectionIcon}><i className={item.icon} aria-hidden /></span>
                  <span className={styles.collectionInfo}>
                    <strong>{item.name}</strong>
                    <span>
                      {item.isSystem && <>{lang('System')} · </>}
                      {lang('$wallets_amount', item.walletsCount)}
                    </span>
                  </span>
                </>
              );

              return (
                <Draggable
                  key={item.id}
                  id={item.id}
                  isDisabled={!canReorder}
                  parentRef={listRef}
                  scrollRef={scrollRef}
                  className={styles.collectionRow}
                  style={buildStyle(`top: ${rowIndex * COLLECTION_ROW_HEIGHT_PX}px`)}
                  onClick={() => undefined}
                  onDrag={handleDrag}
                  onDragEnd={handleDragEnd}
                >
                  <div className={buildClassName(
                    styles.collectionItem,
                    rowIndex < items.length - 1 && styles.collectionItemSeparated,
                    'drag-item',
                  )}
                  >
                    {item.isSystem ? (
                      <div className={buildClassName(
                        styles.collectionContent,
                        canReorder && styles.collectionContentDraggable,
                      )}
                      >
                        {content}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={buildClassName(
                          styles.collectionContent,
                          styles.collectionEdit,
                          canReorder && styles.collectionContentDraggable,
                        )}
                        onClick={() => onEdit(item.collectionId)}
                      >
                        {content}
                      </button>
                    )}
                  </div>
                </Draggable>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}><i className="icon-tag" aria-hidden /></span>
            <strong>{lang('No Collections Yet')}</strong>
            <p>{lang('Create collections to organize and quickly filter your wallets.')}</p>
          </div>
        )}
      </div>

      <div className={styles.managerFooter}>
        <Button isPrimary className={styles.actionButton} onClick={() => onEdit()}>
          <i className="icon-plus" aria-hidden />
          {lang('New Collection')}
        </Button>
      </div>
    </div>
  );
}

export function WalletCollectionEditor({
  isActive,
  collection,
  accounts,
  network,
  onSave,
  onDelete,
  onBack,
  onClose,
}: EditorProps) {
  const lang = useLang();
  const availableAccountIds = new Set(accounts.map(([accountId]) => accountId));
  const [name, setName] = useState(collection?.name ?? '');
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(
    () => new Set(collection?.accountIds.filter((accountId) => availableAccountIds.has(accountId)) ?? []),
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const normalizedName = name.trim();
  const hasNameError = isSubmitted && !normalizedName;
  const hasWalletError = isSubmitted && selectedAccountIds.size === 0;

  useHistoryBack({ isActive, onBack });

  const handleAccountToggle = useLastCallback((accountId: string, isChecked: boolean) => {
    setSelectedAccountIds((current) => {
      const next = new Set(current);
      if (isChecked) next.add(accountId);
      else next.delete(accountId);
      return next;
    });
  });

  const handleSave = useLastCallback(() => {
    setIsSubmitted(true);
    if (!normalizedName || !selectedAccountIds.size) return;

    onSave({
      id: collection?.id,
      name: normalizedName,
      network,
      accountIds: Array.from(selectedAccountIds),
    });
  });

  const handleDelete = useLastCallback(() => {
    if (!collection) return;
    setIsDeleteConfirmationOpen(false);
    onDelete(collection.id);
  });

  return (
    <div className={modalStyles.transitionContentWrapper}>
      <ModalHeader
        title={lang(collection ? 'Edit Collection' : 'New Collection')}
        onBackButtonClick={onBack}
        onClose={onClose}
      />

      <div className={styles.editorContent}>
        <Input
          value={name}
          label={lang('Name')}
          maxLength={40}
          autoComplete="off"
          hasError={hasNameError}
          error={hasNameError ? lang('Enter a collection name.') : undefined}
          onInput={setName}
        />

        <section className={styles.editorSection}>
          <h3>{lang('Select Wallets')}</h3>
          <div className={buildClassName(styles.walletList, hasWalletError && styles.walletListError)}>
            {accounts.map(([accountId, account]) => {
              const title = getAccountTitle(account);

              return (
                <Checkbox
                  key={accountId}
                  checked={selectedAccountIds.has(accountId)}
                  className={styles.walletChoice}
                  contentClassName={styles.walletChoiceContent}
                  onChange={(isChecked) => handleAccountToggle(accountId, isChecked)}
                >
                  <WalletAvatar accountId={accountId} title={title} className={styles.walletAvatar} />
                  <span>{title}</span>
                </Checkbox>
              );
            })}
          </div>
          {hasWalletError && <p className={styles.errorText}>{lang('Select at least one wallet.')}</p>}
        </section>
      </div>

      <div className={styles.editorFooter}>
        {collection && (
          <Button
            isDestructive
            className={styles.editorButton}
            onClick={() => setIsDeleteConfirmationOpen(true)}
          >
            <i className="icon-trash-small" aria-hidden />
            {lang('Delete')}
          </Button>
        )}
        <Button isPrimary className={styles.editorButton} onClick={handleSave}>
          {lang('Save')}
        </Button>
      </div>

      <Modal
        isOpen={isDeleteConfirmationOpen}
        isCompact
        title={lang('Delete Collection')}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <p className={buildClassName(modalStyles.text, modalStyles.text_noExtraMargin)}>
          {lang('Delete this collection? The wallets themselves will not be removed.')}
        </p>
        <div className={modalStyles.footerButtons}>
          <Button onClick={() => setIsDeleteConfirmationOpen(false)}>{lang('Cancel')}</Button>
          <Button isPrimary isDestructive onClick={handleDelete}>{lang('Delete')}</Button>
        </div>
      </Modal>
    </div>
  );
}

export default memo(WalletCollectionsManager);
